import { Input, Label, Textarea } from '@artstorefronts/ui';
import { isMeasurementFrozen, wizardParameter } from './draft';
import { CopyButton } from './CopyButton';
import { TargetEventSelect } from './TargetEventSelect';
import type { WizardDraft } from './types';

function ReadOnlyValue({ id, value }: { id: string; value: string }) {
  return (
    <output
      id={id}
      className="block w-full rounded-md border border-gray-200 bg-gray-50 px-2 py-1.5 font-mono text-sm text-gray-600"
    >
      {value || '—'}
    </output>
  );
}

/**
 * Everything about the wizard that is not a step. There is deliberately NO landing-page field:
 * one fewer decision at authoring time, and no field implying a wizard is tied to a page.
 */
export function WizardFields({
  draft,
  onChange,
}: {
  draft: WizardDraft;
  onChange: (patch: Partial<WizardDraft>) => void;
}) {
  const isCreate = !draft.id;
  const frozen = isMeasurementFrozen(draft);

  return (
    <div className="space-y-5 rounded-lg bg-white p-6 shadow">
      {isCreate && (
        <p className="border-l-2 border-gray-300 pl-3 text-sm leading-relaxed text-gray-600">
          This is the only screen where the slug, the held-out audience and the measured action can be set. Publishing
          freezes all three: the slug because live links depend on it, and the other two because together they are the
          comparison.
        </p>
      )}

      <div>
        <Label htmlFor="wizard-slug" className="mb-1 block text-sm font-medium">
          Slug
        </Label>
        {isCreate ? (
          <Input
            id="wizard-slug"
            type="text"
            className="w-full font-mono text-sm"
            value={draft.slug}
            onChange={(event) => onChange({ slug: event.target.value })}
            placeholder="lowercase-with-hyphens"
          />
        ) : (
          <ReadOnlyValue id="wizard-slug" value={draft.slug} />
        )}
        <p className="mt-1 text-xs text-gray-500">
          {isCreate
            ? 'Set it once. The link you share is built from this and can never change.'
            : "Live links depend on this. It can't change once the wizard exists, and saving never reassigns it."}
        </p>
      </div>

      <div>
        <Label htmlFor="wizard-name" className="mb-1 block text-sm font-medium">
          Name
        </Label>
        <Input
          id="wizard-name"
          type="text"
          className="w-full text-sm"
          value={draft.name}
          onChange={(event) => onChange({ name: event.target.value })}
          placeholder="Meet your communities"
        />
      </div>

      <div>
        <Label htmlFor="wizard-description" className="mb-1 block text-sm font-medium">
          Description
        </Label>
        <Textarea
          id="wizard-description"
          className="w-full text-sm"
          rows={2}
          value={draft.description}
          onChange={(event) => onChange({ description: event.target.value })}
          placeholder="What should this wizard teach, and who is it for?"
        />
      </div>

      <div>
        <p className="mb-1 text-sm font-medium">Add this to any link</p>
        <div className="flex items-start gap-2">
          <code className="min-w-0 flex-1 break-all rounded-md border border-gray-200 bg-gray-50 px-2 py-1.5 text-xs text-gray-800">
            {wizardParameter(draft.slug)}
          </code>
          <CopyButton value={draft.slug ? wizardParameter(draft.slug) : ''} ariaLabel="Copy the wizard parameter" />
        </div>
        <p className="mt-1 text-xs leading-relaxed text-gray-500">
          This wizard opens on whatever page the link is sent to — it is not tied to one. Append this to any ArtHelper
          URL, including a different page per recipient.
        </p>
      </div>

      <div>
        <div className="mb-1 flex flex-wrap items-center gap-2">
          <Label htmlFor="wizard-handoff" className="text-sm font-medium">
            When they finish, send them to
          </Label>
          <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-600">optional</span>
        </div>
        <Input
          id="wizard-handoff"
          type="text"
          className="w-full font-mono text-sm"
          value={draft.handoffHref}
          onChange={(event) => onChange({ handoffHref: event.target.value })}
          placeholder="Leave empty to just close the wizard"
        />
        <p className="mt-1 text-xs leading-relaxed text-gray-500">
          Usually leave this empty. Empty means the wizard closes and the reader stays on the page the link already
          opened, which is usually the page the action happens on. Fill it in only to send them somewhere else — a path
          like /artists, or a full https address.
        </p>
      </div>

      {frozen && (
        <p className="border-l-2 border-amber-300 pl-3 text-sm leading-relaxed text-amber-800">
          This wizard has been published, so the split and the measured action below are frozen together — the
          comparison only stays honest if neither moves. A zero holdout means no lift comparison for this wizard.
        </p>
      )}

      {frozen ? (
        <div>
          <Label htmlFor="wizard-target-event" className="mb-1 block text-sm font-medium">
            Action this wizard should drive
          </Label>
          <ReadOnlyValue id="wizard-target-event" value={draft.targetEventName} />
          <p className="mt-1 text-xs leading-relaxed text-gray-500">
            The Amplitude event we compare between the two audiences — this is how you find out whether the wizard
            worked. Picked from the events the product already sends; the wizard never fires it. Frozen at publish,
            along with the split below.
          </p>
        </div>
      ) : (
        <TargetEventSelect value={draft.targetEventName} onChange={(next) => onChange({ targetEventName: next })} />
      )}

      <div>
        <Label htmlFor="wizard-control-percent" className="mb-1 block text-sm font-medium">
          Held-out audience
        </Label>
        {frozen ? (
          <ReadOnlyValue id="wizard-control-percent" value={`${draft.controlPercent}%`} />
        ) : (
          <div className="flex items-center gap-2">
            <Input
              id="wizard-control-percent"
              type="number"
              min={0}
              max={50}
              className="w-28 text-sm"
              value={draft.controlPercent}
              onChange={(event) => onChange({ controlPercent: event.target.value })}
            />
            <span className="text-sm text-gray-500">%</span>
          </div>
        )}
        <p className="mt-1 text-xs leading-relaxed text-gray-500">
          {frozen
            ? 'Share of people who get the link but no wizard, so we can read real lift. Frozen at publish and up to 50% — changing it under a running comparison would make the numbers unreadable.'
            : 'Share of people who get the link but no wizard, so we can read real lift. Up to 50%. Zero is allowed and means no lift comparison for this wizard.'}
        </p>
      </div>
    </div>
  );
}
